<template>
  <div class="serum-formula">
    <h2 class="serum-formula__title u-typography-helvetica u-text u-text--s">
      <img class="serum-formula__icon" :src="icon" alt="serum-formula-icon" loading="lazy" />

      {{ title }}
    </h2>

    <div class="serum-formula__ingredients ingredients">
      <div
        v-for="{ name, concentration, colorClass } of ingredientsDisplayValues"
        :key="name"
        class="ingredient ingredients__ingredient"
      >
        <span class="ingredient__color-box" :class="colorClass"></span>
        <span class="ingredient__name u-typography-helvetica u-text u-text--xs u-text--s-gte1440">{{
          name
        }}</span>
        <span
          class="ingredient__concentration u-typography-helvetica u-text u-text--link u-text--s"
          >{{ concentration }}</span
        >
      </div>
    </div>
  </div>
</template>

<script>
import { getPackItemUsage, getPackItemIngredients, getPackItemName } from '@/models/core/PackItem';
import { getIngredientTotalConcentration, getIngredientName } from '@/models/core/Ingredient';

import { SERUM_USAGES, PRODUCT_NAMES } from '@/constants/treatment';
import { INGREDIENT_NAME_KEY, INGREDIENT_COLOR_CLASSES } from '@/constants/ingredients';

const FORMULA_TITLE = {
  [SERUM_USAGES.MORNING_ONLY]: 'welcomeSection.usage.morningLabel',
  [SERUM_USAGES.EVENING_ONLY]: 'welcomeSection.usage.eveningLabel',
  [SERUM_USAGES.MORNING_AND_EVENING]: 'welcomeSection.usage.morningAndEveningLabel'
};

const DUPLICATED_FORMULA_TITLE = {
  [SERUM_USAGES.MORNING_ONLY]: 'welcomeSection.usage.duplicatedMorningLabel',
  [SERUM_USAGES.EVENING_ONLY]: 'welcomeSection.usage.duplicatedEveningLabel',
  [SERUM_USAGES.MORNING_AND_EVENING]: 'welcomeSection.usage.duplicatedMorningAndEveningLabel'
};

const ICON_NAME = {
  [SERUM_USAGES.MORNING_ONLY]: 'morning',
  [SERUM_USAGES.EVENING_ONLY]: 'evening',
  [SERUM_USAGES.MORNING_AND_EVENING]: 'morningAndEvening'
};

export default {
  name: 'SerumFormula',
  props: {
    /** PackItem */
    formula: {
      type: Object,
      required: true
    },
    isOneOfTwoFormulas: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      icon: null
    };
  },
  computed: {
    title() {
      const usage = getPackItemUsage(this.formula);
      const isEyeCountourSerum = getPackItemName(this.formula) === PRODUCT_NAMES.EYE_CONTOUR_SERUM;

      if (isEyeCountourSerum) {
        return this.$t(FORMULA_TITLE[usage]);
      }

      return this.isOneOfTwoFormulas
        ? this.$t(FORMULA_TITLE[usage])
        : this.$t(DUPLICATED_FORMULA_TITLE[usage]);
    },
    ingredientsDisplayValues() {
      const ingredients = getPackItemIngredients(this.formula);

      return ingredients.map(ingredient => {
        const concentration = getIngredientTotalConcentration(ingredient);
        const name = getIngredientName(ingredient);

        return {
          name: this.$t(INGREDIENT_NAME_KEY[name]),
          concentration: `${concentration.toFixed(1)}%`,
          colorClass: INGREDIENT_COLOR_CLASSES[name]
        };
      });
    }
  },
  async created() {
    const usage = getPackItemUsage(this.formula);
    const name = ICON_NAME[usage];

    this.icon = (await import(`@/assets/images/icons/${name}.svg`)).default;
  }
};
</script>

<style lang="scss" scoped>
.serum-formula {
  &__title {
    display: flex;
    align-items: baseline;
    color: var(--u-color-grey-600);
  }

  &__icon {
    width: 20px;
    margin-inline-end: 8px;
  }

  &__ingredients {
    margin-top: 16px;
  }
}

.ingredients {
  &__ingredient:not(:last-child) {
    margin-bottom: 16px;
  }
}

.ingredient {
  display: flex;
  align-items: center;

  &__color-box {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    margin-inline-end: 8px;
  }

  &__concentration {
    margin-inline-start: auto;
    background-color: var(--u-color-grey-100);
    border-radius: 40px;
    width: 64px;
    height: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
